import { FC } from 'react'
import { Select, Option } from '@material-tailwind/react'
import { size as Tsize } from '@material-tailwind/react/types/components/select'
import { FiGlobe } from 'react-icons/fi'
import { t } from 'i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { useDarkMode } from '../contexts/DarkModeContext'

interface Language {
  iso: string
  label: string
}

interface FlagIconProps {
  code: string
  className?: string
}

const FlagIcon: FC<FlagIconProps> = ({ code, className = '' }) => {
  const flagUrls: Record<string, string> = {
    en: 'https://flagcdn.com/w40/gb.png',
    fr: 'https://flagcdn.com/w40/fr.png',
    it: 'https://flagcdn.com/w40/it.png',
  }

  if (flagUrls[code]) {
    return (
      <img
        src={flagUrls[code]}
        alt={code}
        className={`w-5 h-4 object-cover rounded-sm mr-2 ${className}`}
        loading="lazy"
      />
    )
  }

  return <FiGlobe className={`w-5 h-4 mr-2 ${className}`} />
}

interface LanguageSelectorProps {
  className?: string
  size?: Tsize
  variant?: 'standard' | 'outlined' | 'static'
  testId?: string
}

const LanguageSelector: FC<LanguageSelectorProps> = ({
  className = '',
  size = 'md',
  variant = 'outlined',
  testId,
}) => {
  const { currentLanguage, setLanguage, availableLanguages } = useLanguage()
  const { darkMode } = useDarkMode()

  const safeCurrentLanguage = currentLanguage || 'en'

  const handleLanguageChange = (value: string | undefined) => {
    if (value && value !== safeCurrentLanguage) {
      setLanguage(value)
    }
  }

  const filteredLanguages: Language[] =
    availableLanguages?.filter((lang) => lang.iso && lang.iso !== 'unset') || []

  return (
    <Select
      label={t('common.language')}
      value={safeCurrentLanguage}
      onChange={handleLanguageChange}
      className={className}
      size={size}
      variant={variant}
      animate={{
        mount: { y: 0 },
        unmount: { y: 25 },
      }}
      menuProps={{ className: 'dark:bg-primary' }}
      {...(darkMode && { color: 'blue-gray' })}
      labelProps={{
        className:
          'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
      }}
      data-testid={testId}
    >
      {filteredLanguages.map((lang) => {
        const isSelected = lang.iso === safeCurrentLanguage
        return (
          <Option
            key={lang.iso}
            value={lang.iso}
            className={`dark:hover:bg-primary-300 ${isSelected ? 'bg-gray-200 dark:bg-primary-300' : ''}`}
          >
            <div className="flex items-center text-primary dark:text-white">
              <FlagIcon code={lang.iso} />
              {lang.label}
            </div>
          </Option>
        )
      })}
    </Select>
  )
}

export default LanguageSelector
